import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppData } from "../../context/AppContext";
import { BananaType, BananaTypeProps } from "../../types/BananaType";
import { SendBananaCard } from "./SendBananaCard";
import toast from "react-hot-toast";
import { BananaApi } from "../../api/BananaApi";
import { Links } from "../../components/utils/Links";
import { MessageType } from "../../types/MessageType";
import { SendBananaMessageView } from "./SendBananaMessageView";
import { logRelayEvent } from "../../analytics/RelayAnalytics";
import { getDefaultMessage, RelayIntent } from "../../relay/RelayPrompts";

interface Props { }
const wait = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const SendBananaPage = ({ }: Props) => {

  const funMessageIndex = useRef(Math.floor(Math.random() * MessageType.SEND_BANANA_FUN_MESSAGES.length));
  const [selectedBanana, setSelectedBanana] = useState<BananaTypeProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { data, setData } = useAppData();
  const [senderName, setSenderName] = useState(data?.user?.name || "");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [showWrapingAnimation, setShowWrapingAnimation] = useState(false);
  const [searchParams] = useSearchParams();
  const appliedHomepageIntent = useRef(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (senderName.trim() === "" && data?.user?.name) {
      setSenderName(data.user.name);
    }
  }, [data?.user?.name]);

  useEffect(() => {
    if (appliedHomepageIntent.current) return;
    const intent = searchParams.get("intent") as RelayIntent | null;
    const intentDefaults: Partial<Record<RelayIntent, string>> = {
      appreciate: "thank-you",
      encourage: "you-got-this",
      reconnect: "thinking-of-you",
    };
    const bananaKey = intent ? intentDefaults[intent] : undefined;
    const banana = bananaKey ? BananaType.getBananaTypeByKey(bananaKey) : undefined;
    if (!intent || !banana || !["appreciate", "encourage", "reconnect"].includes(intent)) return;

    appliedHomepageIntent.current = true;
    setSelectedBanana(banana);
    setMessage(getDefaultMessage(intent));
    logRelayEvent("banana_toss_started", { banana_type: banana.key, homepage_intent: intent });
  }, [searchParams]);

  useEffect(() => {
    if (selectedBanana) return;
    const banana = BananaType.getBananaTypeByKey(searchParams.get("banana") || "");
    if (banana) chooseBanana(banana);
  }, [searchParams]);

  const sendBanana = async () => {
    if (!selectedBanana) return;
    if (data.settings.relayEnabled === false) {
      toast.error("Banana Relay is temporarily paused. Please try again soon.");
      return;
    }

    setIsLoading(true);
    try {
      // Create new banana
      setShowWrapingAnimation(true);
      
      const [res] = await Promise.all([
        BananaApi.createBanana(
          selectedBanana.key,
          false,
          senderName,
          recipientName,
          message
        ),
        wait(4200),
      ]);
      logRelayEvent("banana_root_created", { banana_type: selectedBanana.key, is_public: false });

      setData((prevData) => ({
        ...prevData,
        user: res.user,
      }));
      navigate(Links.getLinkToViewBanana(res.banana, ""));
    } catch (error: any) {
      toast.error("Couldn't wrap your banana. " + error.message);
      setShowWrapingAnimation(false);
      setIsLoading(false);
    }
  }

  const chooseBanana = (banana: BananaTypeProps) => {
    logRelayEvent("banana_toss_started", { banana_type: banana.key });
    setSelectedBanana(banana);
  };

  return (
    <>
      <title>Send Banana | Tuttobanana</title>
      <meta name="description" content="Send a Tuttobanana, a tiny digital gift with a big feeling. Choose your banana, add a personal message, and instantly share joy with anyone, anywhere in the world. Start the smile today!" />
      <div className="bg-gray-100 text-gray-900 px-6 py-20 sm:px-12 overflow-x-hidden">
        <div className="max-w-5xl mx-auto space-y-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-center mb-12 text-yellow-500">
            Send a Tuttobanana 🍌
          </h1>
          {data.settings.relayEnabled === false && <div className="mx-auto max-w-2xl rounded-2xl border border-amber-300 bg-amber-50 p-5 text-center font-semibold text-amber-950">
            Banana Relay is taking a quick peel break. Existing bananas still open normally—please check back soon.
          </div>}
          {!selectedBanana && <>
            <div className="text-center space-y-4">
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-8">
                Pick your perfect banana, add a message, and send a smile that travels the world.
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <CollectionSection title="Everyday Collection" items={BananaType.bananaCollectionEveryday} onSelect={chooseBanana} />
              <CollectionSection title="Celebration Collection" items={BananaType.bananaCollectionCelebration} onSelect={chooseBanana} />
              <CollectionSection title="Care and Comfort Collection" items={BananaType.bananaCollectionCareAndComfort} onSelect={chooseBanana} />
              <CollectionSection title="Seasonal Collection" items={BananaType.bananaCollectionSeasonal} onSelect={chooseBanana} />
            </div>
          </>}
          {!!selectedBanana && (
            <SendBananaMessageView 
              funMessageIndex={funMessageIndex.current}
              isLoading={isLoading}
              isPublic={false}
              senderName={senderName}
              recipientName={recipientName}
              message={message}
              selectedBanana={selectedBanana}
              onCloseClick={setSelectedBanana}
              setSenderName={setSenderName}
              setRecipientName={setRecipientName}
              setMessage={setMessage}
              sendBanana={sendBanana}
              showWrapingAnimation={showWrapingAnimation}
            />
          )}
        </div>
      </div>
    </>
  );
};

interface CollectionSectionProps {
  title: string;
  items: BananaTypeProps[];
  onSelect?: ((item: BananaTypeProps) => void) | any;
}

const CollectionSection: React.FC<CollectionSectionProps> = ({ title, items, onSelect }) => (
  <>
    <h3 className="text-2xl font-semibold mb-4 text-yellow-600">{title}</h3>
    <div className="space-y-4 mb-8">
      {items.map((item) => (
        <SendBananaCard key={item.key} banana={item} onSelect={onSelect} />
      ))}
    </div>
  </>
);

export default SendBananaPage;
